import { Component, Inject, OnInit } from '@angular/core';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { Organization } from 'app/models/organization.model';
import { TranslateService } from '@ngx-translate/core';
import { Group, GroupStructure, Product } from 'app/models/menu.model';
import * as MenuSelectors from 'app/reducers/menu.selectors';
import { Store } from '@ngrx/store';

@UntilDestroy()
@Component({
  selector: 'app-select-products-modal',
  templateUrl: './select-products-modal.component.html',
  styleUrls: ['./select-products-modal.component.scss']
})
export class SelectProductsModalComponent extends BaseModalComponent implements OnInit {

  originalGroups: Group[]  = [];
  originalGroups$: Observable<Map<string, Group>>;
  alreadyAddedProducts: Product[] = [];
  removedAlreadyAddedProductsIds: string[] = [];
  productsToAdd: Product[] = [];
  products: Map<string, Product> = new Map<string, Product>();
  selectedOrganization: Organization;

  isLoading = false;

  products$: Observable<Map<string, Product>>;

  constructor(
    public dialogRef: MatDialogRef<SelectProductsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectProductsModalComponentDataType,
    private translate: TranslateService,
    private store: Store,
  ) {
    super(dialogRef);
    if(data.products && data.products.size > 0) {
      this.products = data.products;
    }
    if(data.groups && data.groups.length > 0) {
      this.originalGroups = data.groups;
    }
    this.selectedOrganization = data.organization;
  }

  get title(): string {
    return this.data.title;
  }

  ngOnInit(): void {
    if(!(this.products && this.products.size > 0 && this.originalGroups && this.originalGroups.length > 0)) {
      this.originalGroups$ = this.store.select(MenuSelectors.selectOriginalDictionaryGroups);
      this.products$ = this.store.select(MenuSelectors.selectDictionaryProducts);combineLatest([
        this.originalGroups$,
        this.products$])
        .pipe(
          untilDestroyed(this)
        )
        .subscribe(([originalGroups, products]) => {
          this.originalGroups = [...originalGroups.values()];

          this.products = products;
        });
    }
    this.alreadyAddedProducts = [];

    for (const productId of this.data.productIds) {
      if (this.products.has(productId)) {
        this.alreadyAddedProducts.push(this.products.get(productId));
      }
    }

    if(!this.selectedOrganization) {
      this.store.select(OrganizationsState.getSelectedOrganization)
        .pipe(
          untilDestroyed(this),
        ).subscribe(org => {
        this.selectedOrganization = org;
      });
    }
  }

  isSelectedProduct(product: Product): boolean {
    const alreadyAdded = this.alreadyAddedProducts.map(p => p.externalId).includes(product.externalId);
    const productShouldAdd = this.productsToAdd.map(p => p.externalId).includes(product.externalId);
    const shouldRemove = this.productShouldRemove(product);
    return ((alreadyAdded && !shouldRemove) || productShouldAdd);
  }

  save(): void {
    let alreadyAddedProductIds = this.alreadyAddedProducts.map(p => p.externalId);
    alreadyAddedProductIds = alreadyAddedProductIds.filter(id => !this.removedAlreadyAddedProductsIds.includes(id));
    const newProductIds = [...alreadyAddedProductIds, ...this.productsToAdd.map(p => p.externalId)];

    this.dialogRef.close({data: newProductIds});
  }

  handleCheckProduct(product: Product, checked: boolean): void {
    if (checked) {
      this.setProduct(product);
    } else {
      this.unsetProduct(product);
    }
  }

  setProduct(product: Product): void {
    if (this.alreadyAddedProducts.some(p => p.externalId === product.externalId)) {
      this.removedAlreadyAddedProductsIds = this.removedAlreadyAddedProductsIds.filter(id => product.externalId !== id);
    } else {
      this.productsToAdd.push(product);
    }
  }

  unsetProduct(product: Product): void {
    if (this.alreadyAddedProducts.some(p => p.externalId === product.externalId)) {
      this.removedAlreadyAddedProductsIds.push(product.externalId);
    } else {
      this.productsToAdd = this.productsToAdd.filter(p => p.externalId !== product.externalId);
    }
  }

  productShouldRemove(product: Product): boolean {
    return this.removedAlreadyAddedProductsIds.some(id => product.externalId === id);
  }

  allDishesFromGroupSelected(groupStructure: GroupStructure): boolean {
    const products = this.getProductsFromGroup(groupStructure);
    return (products.length > 0) && products
      .map(product => this.isSelectedProduct(product))
      .every(selected => selected);
  }

  getProductsFromGroup(groupStructure: GroupStructure): Product[] {
    const products: Product[] = [];
    for (const productInGroup of groupStructure.productsInGroup) {
      if (this.products.has(productInGroup.externalProductId)) {
        products.push(this.products.get(productInGroup.externalProductId));
      }
    }
    return products;
  }

  handleCheckGroup(groupStructure: GroupStructure, checked): void {
    this.getProductsFromGroup(groupStructure).forEach((product) => {
      if (checked) {
        this.setProduct(product);
      } else {
        this.unsetProduct(product);
      }
    });
  }

  getSaveInfo(): string {
    return this.translate.instant('MENU.SECTIONS.SAVE_DISHES_INFO', {
      addingCount: this.productsToAdd.length,
      deletingCount: this.removedAlreadyAddedProductsIds.length
    });
  }
}

export interface SelectProductsModalComponentDataType {
  title: string;
  productIds: string[];
  products: null | Map<string, Product>;
  groups: null | Group[];
  organization: null | Organization;
}
